import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, FileText, CheckCircle2, Info } from "lucide-react";
import { useLang, API } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, SectionHead } from "@/components/motion";

const ALLOWED = ["pdf", "step", "stp", "dxf", "dwg", "iges", "igs", "zip", "jpg", "jpeg", "png", "webp"];
const MAX_SIZE = 20 * 1024 * 1024;

const EMPTY = { company: "", name: "", email: "", phone: "", address: "", service: "", material: "", quantity: "", delivery_date: "", message: "", privacy: false, website: "" };

export default function Inquiry() {
  const { t, lang, pathFor } = useLang();
  useSEO("inquiry");
  const f = t.inquiryPage.form;
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileInput = useRef(null);
  const formTs = useRef(Date.now() / 1000);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addFiles = (list) => {
    const next = [...files];
    for (const file of list) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`${file.name}: ${f.fileBadType}`); continue; }
      if (file.size > MAX_SIZE) { toast.error(`${file.name}: ${f.fileTooLarge}`); continue; }
      if (next.length >= 8) break;
      next.push(file);
    }
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries({ ...form, privacy: String(form.privacy), lang, form_ts: String(formTs.current) }).forEach(([k, v]) => data.append(k, v));
      files.forEach((file) => data.append("files", file));
      await axios.post(`${API}/inquiries`, data);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(t.inquiryPage.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full border border-white/15 bg-anthracite-dark px-4 py-3 text-sm text-white outline-none transition-colors focus:border-royal";
  const labelCls = "mb-2 block font-mono-tech text-xs uppercase tracking-widest text-slate-400";

  return (
    <div className="bg-anthracite-dark pt-16" data-testid="inquiry-page">
      <section className="border-b border-white/10 bg-anthracite grid-lines-dark">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-24">
          <SectionHead kicker={t.inquiryPage.kicker} title={t.inquiryPage.title} light testid="inquiry" />
          <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-slate-400">{t.inquiryPage.intro}</p></Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        {sent ? (
          <Reveal>
            <div className="mx-auto max-w-2xl border border-royal/40 bg-anthracite p-10 text-center" data-testid="inquiry-success">
              <CheckCircle2 size={48} className="mx-auto text-royal-soft" />
              <h2 className="mt-6 text-2xl font-black uppercase tracking-tight text-white">{t.inquiryPage.successTitle}</h2>
              <p className="mt-4 leading-relaxed text-slate-400">{t.inquiryPage.successText}</p>
              <button onClick={() => { setSent(false); setForm(EMPTY); setFiles([]); formTs.current = Date.now() / 1000; }}
                className="mt-8 border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-royal-soft" data-testid="inquiry-new-request">
                {t.inquiryPage.newRequest}
              </button>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
            <Reveal>
              <form onSubmit={submit} className="border border-white/10 bg-anthracite p-6 sm:p-10" data-testid="inquiry-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="inq-company" className={labelCls}>{f.company}</label>
                    <input id="inq-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="inquiry-company" />
                  </div>
                  <div>
                    <label htmlFor="inq-name" className={labelCls}>{f.name}</label>
                    <input id="inq-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="inquiry-name" />
                  </div>
                  <div>
                    <label htmlFor="inq-email" className={labelCls}>{f.email}</label>
                    <input id="inq-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="inquiry-email" />
                  </div>
                  <div>
                    <label htmlFor="inq-phone" className={labelCls}>{f.phone}</label>
                    <input id="inq-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="inquiry-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="inq-address" className={labelCls}>{f.address}</label>
                    <input id="inq-address" value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="inquiry-address" />
                  </div>
                  <div>
                    <label htmlFor="inq-service" className={labelCls}>{f.service}</label>
                    <select id="inq-service" value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls} data-testid="inquiry-service">
                      <option value="">—</option>
                      {f.serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="inq-material" className={labelCls}>{f.material}</label>
                    <select id="inq-material" value={form.material} onChange={(e) => set("material", e.target.value)} className={inputCls} data-testid="inquiry-material">
                      <option value="">—</option>
                      {f.materialOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="inq-quantity" className={labelCls}>{f.quantity}</label>
                    <input id="inq-quantity" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} data-testid="inquiry-quantity" />
                  </div>
                  <div>
                    <label htmlFor="inq-delivery" className={labelCls}>{f.delivery}</label>
                    <input id="inq-delivery" type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="inquiry-delivery" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="inq-message" className={labelCls}>{f.message}</label>
                    <textarea id="inq-message" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={`${inputCls} resize-none`} data-testid="inquiry-message" />
                  </div>

                  <div className="sm:col-span-2">
                    <span className={labelCls}>{f.uploadTitle}</span>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={() => setDrag(false)}
                      onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
                      className={`border border-dashed p-8 text-center transition-colors ${drag ? "border-royal bg-royal/10" : "border-white/20 bg-anthracite-dark"}`}
                      data-testid="inquiry-dropzone"
                    >
                      <UploadCloud size={28} className="mx-auto text-royal-soft" />
                      <p className="mt-3 text-sm text-slate-300">
                        {f.uploadDrag}{" "}
                        <button type="button" onClick={() => fileInput.current?.click()} className="font-semibold text-royal-soft underline underline-offset-4 hover:text-white" data-testid="inquiry-upload-button">
                          {f.uploadButton}
                        </button>
                      </p>
                      <p className="mt-2 font-mono-tech text-[11px] text-slate-500">{f.uploadText}</p>
                      <input ref={fileInput} type="file" multiple accept=".pdf,.step,.stp,.dxf,.dwg,.iges,.igs,.zip,.jpg,.jpeg,.png,.webp" className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="inquiry-file-input" />
                    </div>
                    {files.length > 0 && (
                      <ul className="mt-3 space-y-2" data-testid="inquiry-file-list">
                        {files.map((file, i) => (
                          <li key={`${file.name}-${i}`} className="flex items-center justify-between border border-white/10 bg-anthracite-dark px-4 py-2.5 text-sm">
                            <span className="flex items-center gap-2 truncate text-slate-300"><FileText size={14} className="shrink-0 text-royal-soft" />{file.name}<span className="text-slate-500">({Math.round(file.size / 1024)} KB)</span></span>
                            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-1 text-slate-500 hover:text-white" aria-label="remove" data-testid={`inquiry-file-remove-${i}`}><X size={15} /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-300" data-testid="inquiry-privacy-label">
                      <input type="checkbox" required checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-0.5 h-4 w-4 accent-royal" data-testid="inquiry-privacy" />
                      <span>
                        {f.privacyBefore}{" "}
                        <Link to={pathFor("privacy")} className="text-royal-soft underline underline-offset-2 hover:text-white" data-testid="inquiry-privacy-link">{f.privacyLink}</Link>{" "}
                        {f.privacyAfter}
                      </span>
                    </label>
                  </div>
                  <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                </div>

                <p className="mt-4 font-mono-tech text-xs text-slate-500">{f.requiredNote}</p>
                <button type="submit" disabled={sending} className="mt-6 w-full bg-royal px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover disabled:opacity-50" data-testid="inquiry-submit">
                  {sending ? f.submitting : f.submit}
                </button>
              </form>
            </Reveal>

            <div className="space-y-8 lg:sticky lg:top-28 lg:self-start">
              <Reveal delay={0.08}>
                <div className="border border-royal/40 bg-royal/10 p-6" data-testid="inquiry-hint">
                  <div className="flex items-start gap-3">
                    <Info size={18} className="mt-0.5 shrink-0 text-royal-soft" />
                    <p className="text-sm leading-relaxed text-slate-200">{t.inquiryPage.hint}</p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="border border-white/10 bg-anthracite p-6" data-testid="inquiry-process">
                  <h3 className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{t.inquiryPage.processTitle}</h3>
                  <ol className="mt-5 space-y-4">
                    {t.process.map((s, i) => (
                      <li key={s.title} className="flex gap-4">
                        <span className="font-mono-tech text-sm text-royal-soft">0{i + 1}</span>
                        <div>
                          <p className="text-sm font-semibold text-white">{s.title}</p>
                          <p className="mt-0.5 text-xs text-slate-400">{s.text}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
